"use client";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import Image from "next/image";
import React, { useCallback, useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import { FormatedTime } from "../genericComponents/formated-time/FormatedTime";
import ViewDetails from "../genericComponents/view-details/ViewDetails";
import { AddToCartService } from "../../services/cart";
import { useCurrencyCode } from "../genericComponents/helper-functions/CurrencySymbol";
import { OfferService, ViewDetailsServices } from "@/app/classes/main";

interface QuickCheckOutProps {
  QuickCheckoutData: any;
}

export const QuickCheckOut: React.FC<QuickCheckOutProps> = ({
  QuickCheckoutData,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const [dynamicStyle, setDynamicStyle] = useState("");
  const [addGift, setAddGift] = useState([false]);
  const [open, setOpen] = useState(false);
  const [dataList, setDataList] = useState<ViewDetailsServices[] | undefined>(
    undefined
  );
  const [data, setData] = useState<ViewDetailsServices | undefined>(undefined);
  const [reloadFlag, setReloadFlag] = useState(false);
  const addToCartService = new AddToCartService();
  const currencyCode = useCurrencyCode();
  const [screenWidth, setScreenWidth] = useState(0);

  useEffect(() => {
    // Check if we are in the browser (client-side)
    if (typeof window !== "undefined") {
      // Get the screen width when the component mounts (only on client-side)
      setScreenWidth(window.innerWidth);
    }
  }, []);

  QuickCheckoutData.forEach((service: any) => {
    service.inCart = addToCartService.checkIfAvailableinCart(
      service.id,
      service.cid
    );
  });

  const handleAddGift = async (e: any, index: number) => {
    e.stopPropagation();
    const selectedItemData = dataList && dataList[index];
    if (!selectedItemData) {
      return;
    }
    let inCart = addToCartService.checkIfAvailableinCart(
      selectedItemData?.id,
      selectedItemData?.cid
    );
    if (inCart) {
      await addToCartService.addToCart(
        QuickCheckoutData[index].id,
        false,
        QuickCheckoutData[index].cid,
        QuickCheckoutData[index]?.coverImage.secureHttpPath,
        QuickCheckoutData[index]?.aboutList,
        QuickCheckoutData[index]?.subHeading,
        QuickCheckoutData[index]?.serviceCharge,
        QuickCheckoutData[index]?.productCost,
        QuickCheckoutData[index]?.min,
        QuickCheckoutData[index]?.specialPrice,
        QuickCheckoutData[index]?.specialMessage
      );
    } else {
      await addToCartService.addToCart(
        QuickCheckoutData[index].id,
        true,
        QuickCheckoutData[index].cid,
        QuickCheckoutData[index]?.coverImage.secureHttpPath,
        QuickCheckoutData[index]?.aboutList,
        QuickCheckoutData[index]?.subHeading,
        QuickCheckoutData[index]?.serviceCharge,
        QuickCheckoutData[index]?.productCost,
        QuickCheckoutData[index]?.min,
        QuickCheckoutData[index]?.specialPrice,
        QuickCheckoutData[index]?.specialMessage
      );
    }
    var newDataList = dataList?.map((item: any, index: any) => {
      return {
        ...item,
        inCart: addToCartService.checkIfAvailableinCart(
          dataList[index].id,
          dataList[index].cid
        ),
      };
    });
    setDataList(newDataList);
    setReloadFlag(true);
  };

  useEffect(() => {
    setDataList(QuickCheckoutData);
  }, [QuickCheckoutData]);

  const handleOpenViewDetails = (item: any) => {
    setData(item);
    setOpen(true);
  };

  const handleCardPosition = useCallback(() => {
    if (
      (QuickCheckoutData && QuickCheckoutData?.length > 5) ||
      isSmallScreen ||
      screenWidth < 1425
    ) {
      setDynamicStyle("left");
    } else {
      setDynamicStyle("center");
    }
  }, [QuickCheckoutData, isSmallScreen, screenWidth]);

  useEffect(() => {
    handleCardPosition();
  }, [isSmallScreen, handleCardPosition]);

  return (
    <>
      {dataList && dataList?.length > 0 ? (
        <Box
          sx={{
            marginTop: { xs: "2rem", sm: "3rem" },
            background: "#15741a",
            padding: "0.5rem 0px",
          }}
        >
          <Grid item container xs={12} sm={12} md={12} lg={12} margin="auto">
            <Box
              sx={{
                textAlign: "center",
                flexDirection: "column",
                alignItems: "center",
                width: "100% ",
              }}
            >
              <Box
                sx={{
                  width: "auto",
                  textAlign: "center",
                  color: "white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "start",
                  margin: { xs: "0.5rem auto", sm: "1rem auto" },
                }}
              >
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body1"
                    className="lexend-heading"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "14px", sm: "16px" },
                      color: "white",
                    }}
                  >
                    Quick Checkout
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: dynamicStyle,
                  overflowX: "scroll",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  scrollbarWidth: "none", // Hide scrollbar for Firefox
                  msOverflowStyle: "none", // Hide scrollbar for IE/Edge
                }}
              >
                {dataList?.map((item: any, index: any) => (
                  <Box
                    key={index}
                    onClick={() => handleOpenViewDetails(item)}
                    sx={{
                      cursor: "pointer",
                      minWidth: "16rem",
                      minHeight: "19rem",
                      width: "16rem",
                      height: "19rem",
                      borderRadius: "1rem",
                      overflow: "hidden",
                      position: "relative",
                      margin: { xs: "0.5rem", sm: "1rem" },
                    }}
                  >
                    <Image
                      src={item?.coverImage?.secureHttpPath}
                      width={500}
                      height={500}
                      style={{
                        margin: "auto",
                        objectFit: "cover",
                        width: "16rem",
                        height: "19rem",
                        borderRadius: "1rem",
                        background:
                          "linear-gradient(to bottom, rgba(0, 0, 0, 0.8) 25%, rgba(0, 0, 0, 0))",
                      }}
                      alt="category"
                    />
                    <Box
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        height: "80%",
                        background:
                          "linear-gradient(to top, rgba(0, 0, 0, 0.8) 75%, rgb(255 255 255 / 0%))",
                      }}
                    >
                      <Box
                        sx={{
                          textAlign: "left",
                          padding: "8px",
                          bottom: 0,
                          position: "absolute",
                          width: "-webkit-fill-available",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "white", marginBottom: "-4px" }}
                        >
                          {item?.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "white" }}>
                          {item?.subHeading}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "left",
                            alignItems: "center",
                            color: "white",
                          }}
                        >
                          <WatchLaterIcon sx={{ fontSize: "16px" }} />
                          <Typography
                            variant="caption"
                            sx={{ paddingLeft: "8px" }}
                          >
                            <FormatedTime min={item?.min} />
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            maxHeight: "7rem",
                            overflow: "scroll",
                            "&:: -webkit-scrollbar": {
                              display: "none",
                            },
                          }}
                        >
                          {item?.aboutList?.map(
                            (nestedItem: any, index: any) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "left",
                                  alignItems: "start",
                                }}
                                key={index}
                              >
                                <CheckIcon
                                  sx={{
                                    color: "white",
                                    paddingRight: "5px",
                                    fontSize: "1rem",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: "white", fontSize: "11px" }}
                                >
                                  {nestedItem}
                                </Typography>
                              </Box>
                            )
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "4px",
                          }}
                        >
                          <Typography variant="body2">
                            <span
                              style={{
                                color: "#ffbf00",
                                fontWeight: "bold",
                              }}
                            >
                              {currencyCode}
                              {item?.amount}/-
                            </span>{" "}
                            &nbsp;
                            <span
                              style={{
                                textDecoration: "line-through",
                                color: "#ffbf00",
                              }}
                            >
                              {currencyCode}
                              {item?.mrp}/-
                            </span>
                          </Typography>
                          <Box>
                            {item.inCart == null || item.inCart === false ? (
                              <AddIcon
                                fontSize="small"
                                style={{
                                  height: "1.8rem",
                                  width: "1.8rem",
                                  color: "#15741a",
                                  background: "white",
                                  padding: "4px",
                                  borderRadius: "10rem",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => handleAddGift(e, index)}
                              />
                            ) : (
                              <CheckIcon
                                fontSize="small"
                                style={{
                                  height: "1.8rem",
                                  width: "1.8rem",
                                  color: "#15741a",
                                  background: "white",
                                  padding: "4px",
                                  borderRadius: "10rem",
                                  cursor: "pointer",
                                  border: "1px solid #15741a",
                                }}
                                onClick={(e) => handleAddGift(e, index)}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
          <ViewDetails
            open={open}
            setClose={() => setOpen(false)}
            imageUrl={
              data?.banner?.secureHttpPath ?? data?.coverImage?.secureHttpPath
            }
            heading={data?.name}
            id={data?.id}
            cid={data?.cid}
            servicedDetails={data?.aboutList}
            descriptions={data?.descriptions}
            amount={data?.amount}
            min={data?.min}
            subHeading={data?.subHeading}
            mrp={data?.mrp}
            specialPrice={data?.specialPrice}
            specialMessage={data?.specialMessage}
            coin={data?.cashbackSalontymCoins}
            aboutList={data?.aboutList}
            serviceCharge={data?.serviceCharge}
            productCost={data?.productCost}
            reloadCartItemsFlag={() => {}}
            setReloadViewDetailsItem={() => setReloadFlag(false)}
            reloadViewDetailsItem={reloadFlag}
          />
        </Box>
      ) : (
        ""
      )}
    </>
  );
};
